import Image from 'next/future/image';
import { ProductCategoryWarranty } from '@lambda/apis/src/category/types';
import { ReebeloConditions } from '@lambda/reebelo';
import { handleFreeReturn } from '@lambda/commons/helpers';
import { useState } from 'react';
import { DeliveryDateResponse } from '@/api/products/delivery-date';
import deliveryIcon from '@/public/icons/collection/usps/delivery.svg';
import warrantyIcon from '@/public/icons/collection/usps/warranty.svg';
import freeDeliveryIcon from '@/public/icons/collection/usps/freeDelivery.svg';
import {
  converDateTimeToLocal,
  formatDeliveryDateRange,
} from '@/lib/use-delivery-date';
import { formatWarrantyInfo, normalizeWarrantyLength } from '@/lib/warranty';
import settings from '@/settings';
import { Breadcrumb } from '../NewProductPage/types';
import chevronRightIcon from '@/public/icons/product/chevronRight.svg';
import UspsModal from './Modal';

import auFlag from '@/public/icons/country-flags/AU.svg';
import nzFlag from '@/public/icons/country-flags/NZ.svg';
import sgFlag from '@/public/icons/country-flags/SG.svg';
import usFlag from '@/public/icons/country-flags/US.svg';
import caFlag from '@/public/icons/country-flags/CA.svg';

const easyReturnCategories = [
  'Golf Shoes',
  'Hiking Shoes',
  'Fashion & Jewelry',
  'Fashion & Accessories',
  "Baby's Fashion",
];

const Usps = ({
  deliveryDate,
  productCondition,
  categoryWarrantyInfo,
  isLocalVendor,
  breadcrumb,
}: {
  deliveryDate: DeliveryDateResponse | undefined;
  productCondition: string;
  categoryWarrantyInfo?: ProductCategoryWarranty | null;
  isLocalVendor: boolean;
  breadcrumb: Breadcrumb;
}) => {
  const [showModal, setShowModal] = useState(false);

  const freeReturnDays = handleFreeReturn(
    settings.store,
    settings.free_return_days,
  );

  const formattedDeliveryDate = formatDeliveryDateRange(
    deliveryDate?.standard,
    'compact',
  );
  const formattedExpressDeliveryDate = formatDeliveryDateRange(
    deliveryDate?.express,
    'compact',
  );

  const isRefurbishedAndSG =
    settings.store === 'quista' &&
    productCondition !== ReebeloConditions.BrandNew;

  const formattedWarrantyInfo = formatWarrantyInfo(categoryWarrantyInfo);

  const warrantyLengthInDays = normalizeWarrantyLength(
    categoryWarrantyInfo?.warrantyLength ?? settings.warranty_month,
    categoryWarrantyInfo?.warrantyUnit ?? 'month',
  );

  const warrantyIsLessThanFreeReturnDays =
    warrantyLengthInDays <= freeReturnDays;

  const categoryWarranty = isRefurbishedAndSG
    ? '3 Months'
    : formattedWarrantyInfo;

  const maxExpressDeliveryDate = deliveryDate?.express?.maxDeliveryDate
    ? converDateTimeToLocal(deliveryDate?.express?.maxDeliveryDate)
    : null;

  function formatOrigin(): {
    origin: string;
    flag: any;
  } {
    if (!isLocalVendor) return { origin: '', flag: null };

    switch (settings.store) {
      case 'reebelo-us':
        return { flag: usFlag, origin: 'in the US' };
      case 'reebelo-nz':
        return { flag: nzFlag, origin: 'in New Zealand' };
      case 'quista':
        return { flag: sgFlag, origin: 'in Singapore' };
      case 'reebelo-au':
        return { flag: auFlag, origin: 'in Australia' };
      case 'reebelo-ca':
        return { flag: caFlag, origin: 'in Canada' };
      default:
        return { flag: null, origin: '' };
    }
  }

  const formattedOrigin = formatOrigin();

  const shouldShowEasyReturn =
    ['reebelo-us', 'reebelo-nz'].includes(settings.store) &&
    breadcrumb.some((b) => easyReturnCategories.includes(b.name));

  return (
    <div className="bg-[#ECFCF5] text-xxs font-light sm:text-xs lg:rounded-md">
      <div className="flex w-full justify-around gap-1.5 p-3 sm:px-6 sm:py-4 lg:justify-between lg:p-3 xl:px-6 xl:py-4">
        {/* Delivery Info */}
        <div className="flex flex-col items-center text-center sm:flex-row sm:text-left lg:flex-col lg:items-center lg:text-center xl:flex-row xl:text-left">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-700 sm:mr-2">
            <div className="relative h-7 w-7">
              <Image
                src={deliveryIcon}
                height={20}
                width={20}
                alt="Delivery"
                className="h-full w-full"
              />
            </div>
          </div>
          <div className="mt-2 leading-3 sm:mt-0 sm:space-y-1 lg:mt-2 xl:mt-0">
            {formattedDeliveryDate ? (
              <div>
                {shouldShowEasyReturn ? 'Free delivery by' : 'Get it by'}{' '}
                {formattedDeliveryDate}
              </div>
            ) : (
              <div>Free Delivery</div>
            )}
            {maxExpressDeliveryDate && (
              <div>
                <button
                  onClick={() => {
                    const element = document.getElementById('shipping-options');

                    if (element) {
                      window.scrollTo({
                        top: element.offsetTop,
                        behavior: 'smooth',
                      });
                    }
                  }}
                  className="underline"
                >
                  Faster Delivery
                </button>{' '}
                by {formattedExpressDeliveryDate}
              </div>
            )}
          </div>
        </div>
        {/*  Warranty/Return & Battery */}
        <div className="flex flex-col items-center text-center sm:flex-row sm:text-left lg:flex-col lg:items-center lg:text-center xl:flex-row xl:text-left">
          <div className="flex h-7 w-7  shrink-0 items-center justify-center rounded-md bg-gray-700 sm:mr-2">
            <div className="relative h-5 w-5">
              <Image
                src={warrantyIcon}
                height={20}
                width={20}
                alt="Warranty and Return"
                className="h-full w-full"
              />
            </div>
          </div>
          <div className="mt-2 leading-3 sm:mt-0 sm:space-y-1 lg:mt-2 xl:mt-0">
            {shouldShowEasyReturn ? (
              <div>Easy Returns</div>
            ) : (
              <>
                {!warrantyIsLessThanFreeReturnDays && (
                  <div className="capitalize">
                    {categoryWarranty || `${settings.warranty_month} months`}{' '}
                    warranty
                  </div>
                )}
                <div>Battery Guarantee</div>
              </>
            )}
          </div>
        </div>
        {/* Free Shipping & Returns */}
        <div className="flex flex-col items-center text-center sm:flex-row sm:text-left lg:flex-col lg:items-center lg:text-center xl:flex-row xl:text-left">
          <div className="flex h-7 w-7  shrink-0 items-center justify-center rounded-md bg-gray-700 sm:mr-2">
            <div className="relative -mt-0.5 h-5 w-5">
              <Image
                src={freeDeliveryIcon}
                height={20}
                width={20}
                alt="Free Shipping"
                className="h-full w-full"
              />
            </div>
          </div>
          <div className="mt-2 leading-3 sm:mt-0 sm:space-y-1 lg:mt-2 xl:mt-0">
            <div>Free Shipping</div>
            <div>Free {freeReturnDays}-Day Returns</div>
          </div>
        </div>
      </div>
      {/* Footer + Modal */}
      <button
        onClick={() => setShowModal(true)}
        className="flex w-full items-center justify-center gap-2 bg-[#E2F1EA] px-3 py-1.5 outline-none lg:rounded-b-md"
      >
        {formattedOrigin.flag && (
          <Image
            alt={formattedOrigin.origin}
            src={formattedOrigin.flag}
            height={50}
            width={75}
            className="h-3.5 w-auto shrink-0"
          />
        )}
        <div>
          Certified Refurbished
          {formattedOrigin?.origin
            ? ` ${formattedOrigin?.origin}`
            : '. Guaranteed Quality'}
          . See all Benefits.
        </div>
        <Image
          alt="See all Benefits"
          src={chevronRightIcon}
          height={20}
          width={20}
          className="h-2.5 w-2.5 shrink-0"
        />
      </button>
      <UspsModal
        showModal={showModal}
        hideModal={() => setShowModal(false)}
        warranty={categoryWarranty || `${settings.warranty_month} Months`}
      />
    </div>
  );
};

export default Usps;
